import { Fragment, type ReactNode, useCallback } from 'react';
import { parseMarkedText, type TextMarker } from '@/shared/lib';
import { RichTextContent } from '@/shared/ui';

type Props<T> = {
  /** Комментарий с разметкой */
  html: string;
  markers: TextMarker<T>[];
  renderMarker: (marker: T, index: number) => ReactNode;
  /**
   * Рендер текста между тегами; `offset` — позиция этого текста в тексте всего комментария
   * без разметки. Без него текст рисуется обычным, с распознанными маркерами.
   */
  renderText?: (text: string, offset: number) => ReactNode;
};

/**
 * Комментарий с HTML-разметкой: теги отрисовываются, маркеры внутри текста остаются кликабельными.
 *
 * Разметку рендерит {@link RichTextContent}; здесь к нему добавляется разбор текста на маркеры
 * (таймкоды, ссылки на правила и узлы знаний).
 */
export const HtmlCommentContent = <T,>({ html, markers, renderMarker, renderText }: Props<T>) => {
  /** Текстовая нода: обычные фрагменты вперемешку с маркерами */
  const renderContentText = useCallback(
    (text: string, offset: number): ReactNode =>
      renderText
        ? renderText(text, offset)
        : parseMarkedText(text, markers).map((part, index) =>
            part.kind === 'text' ? <Fragment key={index}>{part.text}</Fragment> : renderMarker(part.marker, index),
          ),
    [markers, renderMarker, renderText],
  );

  return <RichTextContent html={html} renderText={renderContentText} />;
};
